import React, { useMemo, useState } from 'react';
import { DndContext } from "@dnd-kit/core";
import { PickerListItem } from "@v2/components/controls/PickerList/PickerListItem/PickerListItem";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core/dist/types";
import { useStyles } from "./PickerList.styles";
import { PrimaryButton } from "@fluentui/react";
import { SearchPage } from "@v2/components/controls/PickerList/search/SearchPage/SearchPage";
import { IdGroup } from "@v2/components/controls/PickerList/search/SearchItemList/SearchItemList";
import { SearchModal } from "@v2/components/controls/PickerList/search/SearchModal/SearchModal";

export enum SearchMode {
  Modal,
  Page,
}

interface PickerListProps <T> {
  items: T[];
  selectedIds: any[];
  onSelectedIdsChange: (selectedIds: any[]) => void;
  getItemId?: (item: T) => string;
  getItemLabel?: (item: T) => string;
  idGroups?: IdGroup[];
  maxHeight?: number;
  disabled?: boolean;
  searchMode?: SearchMode;
  searchTitle?: string;
}

export const PickerList = function <T> (props: PickerListProps<T>) {
  const {
    items = [],
    selectedIds = [],
    idGroups = [],
    onSelectedIdsChange,
    maxHeight,
    disabled,
    searchMode = SearchMode.Page,
    searchTitle,
  } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const classes = useStyles(maxHeight);

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;
    if (over?.id && over.id !== active.id) {
      const oldIndex = selectedIds.findIndex((id: any) => active.id === id);
      const newIndex = selectedIds.findIndex((id: any) => (over as any).id === id);
      const newSelectedIds = arrayMove(selectedIds, oldIndex, newIndex);
      onSelectedIdsChange(newSelectedIds);
    }
  }

  const handleItemDelete = (id: string) => {
    const newSelection = [...selectedIds];
    const index = newSelection.indexOf(id);
    if (index !== -1) {
      newSelection.splice(index, 1);
      onSelectedIdsChange(newSelection);
    }
  }

  const itemMap = useMemo(() =>
    items.reduce((map, item: any) => map.set(item.id, item), new Map<any, T>()),
    [items, selectedIds]
  );

  const renderListItemById = (id: any) => {
    const item: any = itemMap.get(id);
    if (!item) {
      return null;
    }
    return (
      <PickerListItem id={id} label={item.label} key={id} onDeleteClick={() => handleItemDelete(id)}/>
    )
  }

  const handleSelectionChange = (newSelection: string[]) => {
    onSelectedIdsChange(newSelection);
  }

  const handleApply = (ids: string[]) => {
    onSelectedIdsChange(ids);
  }

  return (
    <div className={classes.pickerList}>
      <DndContext
        onDragEnd={handleDragEnd}
      >
        {selectedIds.length > 0
          ? (
            <div className={classes.scrollable}>
              <SortableContext
                items={selectedIds}
                strategy={verticalListSortingStrategy}
              >
                {selectedIds.map(renderListItemById)}
              </SortableContext>
            </div>
          )
          : <div className={classes.noItemMessage}>No fields selected</div>
        }
      </DndContext>
      {searchMode === SearchMode.Modal && (
        <SearchModal
          isOpen={isModalOpen}
          onDismiss={() => setIsModalOpen(false)}
          items={items}
          selectedIds={selectedIds}
          onSelectionChange={handleSelectionChange}
          idGroups={idGroups}
        />
      )}
      {searchMode === SearchMode.Page && (
        <SearchPage
          idGroups={idGroups}
          title={searchTitle}
          isOpen={isModalOpen}
          items={items}
          selectedIds={selectedIds}
          onSelectionChange={() => {}}
          onDismiss={() => setIsModalOpen(false)}
          onApply={handleApply}
        />
      )}
      <PrimaryButton
        onClick={() => setIsModalOpen(true)}
        disabled={disabled}
      >
        Add fields
      </PrimaryButton>
    </div>
  )
}
